<template>
  <div class="tender-overview-panel">
    <!-- Key Metrics Row -->
    <div class="metrics-row q-mb-lg">
      <div class="row q-col-gutter-md">
        <!-- Status Overview -->
        <div class="col-12 col-md-3">
          <q-card flat bordered class="metric-card clickable" @click.stop>
            <q-card-section class="text-center">
              <q-icon
                :name="getStatusIcon(tender.status)"
                :color="getStatusColor(tender.status)"
                size="2.5rem"
                class="q-mb-sm"
              />
              <div class="metric-label">Status</div>
              <div class="metric-value editable-value" :class="`text-${getStatusColor(tender.status)}`">
                {{ getStatusLabel(tender.status) }}
                <q-popup-edit v-slot="scope" v-model="editableStatus" auto-save @save="saveStatus">
                  <q-select
                    v-model="scope.value"
                    :options="statusOptions"
                    emit-value
                    map-options
                    dense
                    autofocus
                  />
                </q-popup-edit>
                <q-icon name="edit" size="xs" class="edit-icon q-ml-xs" />
              </div>
            </q-card-section>
          </q-card>
        </div>

        <!-- Win Likelihood -->
        <div class="col-12 col-md-3">
          <q-card flat bordered class="metric-card clickable" @click.stop>
            <q-card-section class="text-center">
              <div class="likelihood-display">
                <q-circular-progress
                  :value="tender.win_likelihood || 0"
                  :color="getLikelihoodColor(tender.win_likelihood || 0)"
                  size="60px"
                  :thickness="0.15"
                  class="q-mb-sm"
                />
                <div class="likelihood-text">
                  {{ tender.win_likelihood || 0 }}%
                </div>
              </div>
              <div class="metric-label">Win Likelihood</div>
              <div class="metric-value editable-value" :class="`text-${getLikelihoodColor(tender.win_likelihood || 0)}`">
                {{ getLikelihoodLabel(tender.win_likelihood || 0) }}
                <q-popup-edit v-slot="scope" v-model="editableWinLikelihood" auto-save @save="saveWinLikelihood">
                  <q-slider
                    v-model="scope.value"
                    :min="0"
                    :max="100"
                    :step="10"
                    label
                    label-always
                    color="primary"
                    style="min-width: 200px"
                  />
                </q-popup-edit>
                <q-icon name="edit" size="xs" class="edit-icon q-ml-xs" />
              </div>
            </q-card-section>
          </q-card>
        </div>

        <!-- Project Value -->
        <div class="col-12 col-md-3">
          <q-card flat bordered class="metric-card clickable" @click.stop>
            <q-card-section class="text-center">
              <q-icon name="attach_money" color="positive" size="2.5rem" class="q-mb-sm" />
              <div class="metric-label">Estimated Value</div>
              <div class="metric-value editable-value text-positive">
                {{ tender.estimated_value ? `$${formatCurrency(tender.estimated_value)}` : 'Not set' }}
                <q-popup-edit v-slot="scope" v-model="editableEstimatedValue" auto-save @save="saveEstimatedValue">
                  <q-input v-model="scope.value" type="text" prefix="$" dense autofocus placeholder="e.g. 30,000" />
                </q-popup-edit>
                <q-icon name="edit" size="xs" class="edit-icon q-ml-xs" />
              </div>
            </q-card-section>
          </q-card>
        </div>

        <!-- Days Remaining -->
        <div class="col-12 col-md-3">
          <q-card flat bordered class="metric-card clickable" @click.stop>
            <q-card-section class="text-center">
              <q-icon
                name="schedule"
                :color="getDueDateColor(tender.due_date)"
                size="2.5rem"
                class="q-mb-sm"
              />
              <div class="metric-label">Due Date</div>
              <div class="metric-value editable-value" :class="`text-${getDueDateColor(tender.due_date)}`">
                {{ getDaysRemaining(tender.due_date) }}
                <q-popup-edit v-slot="scope" v-model="editableDueDate" auto-save @save="saveDueDate">
                  <q-date v-model="scope.value" mask="YYYY-MM-DD" />
                </q-popup-edit>
                <q-icon name="edit" size="xs" class="edit-icon q-ml-xs" />
              </div>
            </q-card-section>
          </q-card>
        </div>
      </div>
    </div>

    <!-- Main Content Grid -->
    <div class="content-grid">
      <div class="row q-col-gutter-lg">
        <!-- Left Column -->
        <div class="col-12 col-lg-8">
          <!-- Project Summary -->
          <q-card flat bordered class="overview-section q-mb-lg">
            <q-card-section>
              <div class="section-header">
                <q-icon name="info" color="primary" class="q-mr-sm" />
                <div class="section-title">Project Summary</div>
              </div>
              
              <div class="summary-grid">
                <div class="summary-item">
                  <div class="summary-label">Site</div>
                  <div class="summary-value">
                    <span class="editable-value" @click.stop>
                      {{ tender.site_name || 'Unknown Site' }}
                      <q-popup-edit v-slot="scope" v-model="editableSiteId" auto-save @save="saveSite">
                        <q-select
                          v-model="scope.value"
                          :options="siteOptions"
                          emit-value
                          map-options
                          dense
                          autofocus
                          @keyup.enter="scope.set"
                        />
                      </q-popup-edit>
                      <q-icon name="edit" size="xs" class="edit-icon q-ml-xs" />
                    </span>
                  </div>
                </div>
                
                <div class="summary-item">
                  <div class="summary-label">Project Type</div>
                  <div class="summary-value">
                    <span class="editable-value" @click.stop>
                      {{ formatProjectType(tender.project_type) || 'Not set' }}
                      <q-popup-edit v-slot="scope" v-model="editableProjectType" auto-save @save="saveProjectType">
                        <q-select
                          v-model="scope.value"
                          :options="projectTypeOptions"
                          emit-value
                          map-options
                          dense
                          autofocus
                          @keyup.enter="scope.set"
                        />
                      </q-popup-edit>
                      <q-icon name="edit" size="xs" class="edit-icon q-ml-xs" />
                    </span>
                  </div>
                </div>
                
                <div class="summary-item">
                  <div class="summary-label">Priority</div>
                  <div class="summary-value">
                    <span class="editable-value" @click.stop>
                      <q-badge
                        :color="getPriorityColor(tender.priority)"
                        :label="formatPriority(tender.priority) || 'Not set'"
                      />
                      <q-popup-edit v-slot="scope" v-model="editablePriority" auto-save @save="savePriority">
                        <q-select
                          v-model="scope.value"
                          :options="priorityOptions"
                          emit-value
                          map-options
                          dense
                          autofocus
                          @keyup.enter="scope.set"
                        />
                      </q-popup-edit>
                      <q-icon name="edit" size="xs" class="edit-icon q-ml-xs" />
                    </span>
                  </div>
                </div>
                
                <div class="summary-item">
                  <div class="summary-label">Reference</div>
                  <div class="summary-value">
                    <span class="editable-value" @click.stop>
                      {{ tender.reference_number || 'Not set' }}
                      <q-popup-edit v-slot="scope" v-model="editableReference" auto-save @save="saveReference">
                        <q-input v-model="scope.value" dense autofocus @keyup.enter="scope.set" />
                      </q-popup-edit>
                      <q-icon name="edit" size="xs" class="edit-icon q-ml-xs" />
                    </span>
                  </div>
                </div>
              </div>
              
              <div class="description-section">
                <div class="description-label">Description</div>
                <div class="description-text editable-value" @click.stop>
                  {{ tender.description || 'No description' }}
                  <q-popup-edit v-slot="scope" v-model="editableDescription" auto-save @save="saveDescription">
                    <q-input v-model="scope.value" type="textarea" dense autofocus rows="3" />
                  </q-popup-edit>
                  <q-icon name="edit" size="xs" class="edit-icon q-ml-xs" />
                </div>
              </div>
            </q-card-section>
          </q-card>

          <!-- Pipeline Information -->
          <q-card flat bordered class="overview-section q-mb-lg">
            <q-card-section>
              <div class="section-header">
                <q-icon name="trending_up" color="teal" class="q-mr-sm" />
                <div class="section-title">Pipeline Information</div>
              </div>

              <div class="summary-grid">
                <div class="summary-item">
                  <div class="summary-label">Market Sector</div>
                  <div class="summary-value">
                    <q-badge
                      v-if="tender.market_sector"
                      :color="tender.market_sector?.color || 'primary'"
                      :label="tender.market_sector?.name || 'Not set'"
                    >
                      <q-icon
                        v-if="tender.market_sector?.icon"
                        :name="tender.market_sector.icon"
                        class="q-mr-xs"
                        size="14px"
                      />
                    </q-badge>
                    <span v-else class="text-grey-6">Not set</span>
                  </div>
                </div>

                <div class="summary-item">
                  <div class="summary-label">Tender Type</div>
                  <div class="summary-value">
                    {{ formatTenderType(tender.tender_type) || 'Not set' }}
                  </div>
                </div>

                <div class="summary-item">
                  <div class="summary-label">Construction Value</div>
                  <div class="summary-value">
                    {{ tender.construction_value ? `$${formatCurrency(tender.construction_value)}` : 'Not set' }}
                  </div>
                </div>

                <div class="summary-item">
                  <div class="summary-label">Default Markup</div>
                  <div class="summary-value">
                    {{ tender.default_markup ? `${tender.default_markup}%` : 'Not set' }}
                  </div>
                </div>
              </div>
            </q-card-section>
          </q-card>

          <!-- Organizations -->
          <q-card flat bordered class="overview-section q-mb-lg">
            <q-card-section>
              <div class="section-header">
                <q-icon name="business" color="purple" class="q-mr-sm" />
                <div class="section-title">Organizations</div>
              </div>

              <div class="summary-grid">
                <div class="summary-item">
                  <div class="summary-label">Consultant</div>
                  <div class="summary-value">
                    <template v-if="tender.consultant">
                      <span>{{ tender.consultant.name }}</span>
                      <q-btn
                        v-if="tender.consultant.email"
                        flat
                        dense
                        size="sm"
                        icon="mail"
                        class="q-ml-xs"
                        @click="emailContact(tender.consultant.email)"
                      >
                        <q-tooltip>Email {{ tender.consultant.email }}</q-tooltip>
                      </q-btn>
                    </template>
                    <span v-else class="text-grey-6">Not assigned</span>
                  </div>
                </div>

                <div class="summary-item">
                  <div class="summary-label">Builder</div>
                  <div class="summary-value">
                    <template v-if="tender.builder">
                      <span>{{ tender.builder.name }}</span>
                    </template>
                    <span v-else class="text-grey-6">Not assigned</span>
                  </div>
                </div>

                <div class="summary-item">
                  <div class="summary-label">Builder Status</div>
                  <div class="summary-value">
                    <q-badge
                      v-if="tender.builder_status"
                      :color="getBuilderStatusColor(tender.builder_status)"
                      :label="formatBuilderStatus(tender.builder_status)"
                    />
                    <span v-else class="text-grey-6">Not set</span>
                  </div>
                </div>
              </div>
            </q-card-section>
          </q-card>

          <!-- Technical Flags -->
          <q-card flat bordered class="overview-section q-mb-lg">
            <q-card-section>
              <div class="section-header">
                <q-icon name="engineering" color="cyan" class="q-mr-sm" />
                <div class="section-title">Technical Flags</div>
              </div>

              <div class="summary-grid">
                <div class="summary-item">
                  <div class="summary-label">BMS Specified</div>
                  <div class="summary-value">
                    {{ formatFieldValue(tender.bms_specified) }}
                  </div>
                </div>

                <div class="summary-item">
                  <div class="summary-label">Tridium Specified</div>
                  <div class="summary-value">
                    {{ formatFieldValue(tender.tridium_specified) }}
                  </div>
                </div>

                <div class="summary-item">
                  <div class="summary-label">BPIC Site</div>
                  <div class="summary-value">
                    {{ formatFieldValue(tender.bpic_site) }}
                  </div>
                </div>

                <div class="summary-item">
                  <div class="summary-label">EBA Site</div>
                  <div class="summary-value">
                    {{ formatFieldValue(tender.eba_site) }}
                  </div>
                </div>

                <div class="summary-item">
                  <div class="summary-label">Includes Install</div>
                  <div class="summary-value">
                    {{ formatFieldValue(tender.includes_install) }}
                  </div>
                </div>

                <div class="summary-item">
                  <div class="summary-label">Mech/Elec</div>
                  <div class="summary-value">
                    {{ formatFieldValue(tender.mech_elec) }}
                  </div>
                </div>

                <div class="summary-item">
                  <div class="summary-label">Mech/Elec Status</div>
                  <div class="summary-value">
                    {{ formatFieldValue(tender.mech_elec_status) }}
                  </div>
                </div>
              </div>
            </q-card-section>
          </q-card>

          <!-- Assignment -->
          <q-card flat bordered class="overview-section q-mb-lg">
            <q-card-section>
              <div class="section-header">
                <q-icon name="assignment_ind" color="indigo" class="q-mr-sm" />
                <div class="section-title">Assignment</div>
              </div>

              <div class="summary-grid">
                <div class="summary-item">
                  <div class="summary-label">Estimator</div>
                  <div class="summary-value">
                    {{ tender.estimator?.name || 'Not assigned' }}
                  </div>
                </div>

                <div class="summary-item">
                  <div class="summary-label">Estimator 2</div>
                  <div class="summary-value">
                    {{ tender.estimator_2?.name || 'Not assigned' }}
                  </div>
                </div>

                <div class="summary-item">
                  <div class="summary-label">Project Manager</div>
                  <div class="summary-value">
                    {{ tender.project_manager?.name || 'Not assigned' }}
                  </div>
                </div>
              </div>
            </q-card-section>
          </q-card>

          <!-- Additional Dates -->
          <q-card flat bordered class="overview-section q-mb-lg">
            <q-card-section>
              <div class="section-header">
                <q-icon name="event_note" color="deep-orange" class="q-mr-sm" />
                <div class="section-title">Additional Dates</div>
              </div>

              <div class="summary-grid">
                <div class="summary-item">
                  <div class="summary-label">Requested Date</div>
                  <div class="summary-value">
                    {{ tender.requested_date ? formatDate(tender.requested_date) : 'Not set' }}
                  </div>
                </div>

                <div class="summary-item">
                  <div class="summary-label">Expected Order Date</div>
                  <div class="summary-value">
                    {{ tender.expected_order_date ? formatDate(tender.expected_order_date) : 'Not set' }}
                  </div>
                </div>

                <div class="summary-item">
                  <div class="summary-label">Indicative Program</div>
                  <div class="summary-value">
                    {{ tender.indicative_program || 'Not set' }}
                  </div>
                </div>
              </div>
            </q-card-section>
          </q-card>

          <!-- Quick Actions -->
          <q-card flat bordered class="overview-section">
            <q-card-section>
              <div class="section-header">
                <q-icon name="flash_on" color="accent" class="q-mr-sm" />
                <div class="section-title">Quick Actions</div>
              </div>

              <div class="actions-grid">
                <q-btn
                  color="primary"
                  icon="people"
                  label="Add Contractor"
                  class="action-btn"
                  @click="$emit('add-contractor')"
                />

                <q-btn
                  color="secondary"
                  icon="description"
                  label="Add Quote"
                  class="action-btn"
                  @click="$emit('add-quote')"
                />

                <q-btn
                  color="orange"
                  icon="email"
                  label="Send Email"
                  class="action-btn"
                  @click="sendEmail"
                />
              </div>
            </q-card-section>
          </q-card>
        </div>

        <!-- Right Column -->
        <div class="col-12 col-lg-4">
          <!-- Timeline -->
          <q-card flat bordered class="overview-section q-mb-lg">
            <q-card-section>
              <div class="section-header">
                <q-icon name="schedule" color="warning" class="q-mr-sm" />
                <div class="section-title">Timeline</div>
              </div>
              
              <q-timeline color="primary" class="timeline">
                <q-timeline-entry
                  icon="add"
                  color="grey"
                  :subtitle="formatDate(tender.created_at)"
                >
                  <div>Tender Created</div>
                </q-timeline-entry>
                
                <q-timeline-entry
                  v-if="tender.submission_date"
                  icon="send"
                  color="blue"
                  :subtitle="formatDate(tender.submission_date)"
                >
                  <div>Tender Submitted</div>
                </q-timeline-entry>
                
                <q-timeline-entry
                  v-if="tender.due_date"
                  icon="event"
                  :color="getDueDateColor(tender.due_date)"
                  :subtitle="formatDate(tender.due_date)"
                >
                  <div>Due Date</div>
                  <div class="text-caption">{{ getRelativeDate(tender.due_date) }}</div>
                </q-timeline-entry>
                
                <q-timeline-entry
                  v-if="tender.expected_start_date"
                  icon="play_arrow"
                  color="positive"
                  :subtitle="formatDate(tender.expected_start_date)"
                >
                  <div>Expected Start</div>
                </q-timeline-entry>
              </q-timeline>
            </q-card-section>
          </q-card>

          <!-- Last Followup -->
          <q-card
            v-if="tender.last_followup || tender.last_followup_by"
            flat
            bordered
            class="overview-section q-mb-lg"
          >
            <q-card-section>
              <div class="section-header">
                <q-icon name="contact_phone" color="orange" class="q-mr-sm" />
                <div class="section-title">Last Followup</div>
              </div>

              <div class="last-followup-content">
                <div v-if="tender.last_followup" class="followup-date">
                  <q-icon name="event" color="grey-7" size="18px" class="q-mr-sm" />
                  <span class="text-weight-bold">{{ formatDate(tender.last_followup) }}</span>
                  <span class="text-grey-7 q-ml-sm">{{ getRelativeDate(tender.last_followup) }}</span>
                </div>
                <div v-if="tender.last_followup_by" class="followup-user q-mt-sm">
                  <q-icon name="person" color="grey-7" size="18px" class="q-mr-sm" />
                  <span>{{ tender.last_followup_by?.name || tender.last_followup_by }}</span>
                </div>
              </div>
            </q-card-section>
          </q-card>

          <!-- Progress Tracker -->
          <q-card flat bordered class="overview-section">
            <q-card-section>
              <div class="section-header">
                <q-icon name="trending_up" color="positive" class="q-mr-sm" />
                <div class="section-title">Progress</div>
              </div>
              
              <div class="progress-items">
                <div class="progress-item">
                  <div class="progress-header">
                    <span>Contractors Added</span>
                    <span class="progress-count">{{ contractorCount }}/{{ targetContractorCount }}</span>
                  </div>
                  <q-linear-progress
                    :value="contractorProgress"
                    color="primary"
                    size="8px"
                    rounded
                  />
                </div>
                
                <div class="progress-item">
                  <div class="progress-header">
                    <span>Quotes Created</span>
                    <span class="progress-count">{{ quoteCount }}/{{ targetQuoteCount }}</span>
                  </div>
                  <q-linear-progress
                    :value="quoteProgress"
                    color="secondary"
                    size="8px"
                    rounded
                  />
                </div>
                
                <div class="progress-item">
                  <div class="progress-header">
                    <span>Documentation</span>
                    <span class="progress-count">{{ documentCount }}/{{ targetDocumentCount }}</span>
                  </div>
                  <q-linear-progress
                    :value="documentProgress"
                    color="accent"
                    size="8px"
                    rounded
                  />
                </div>
              </div>
            </q-card-section>
          </q-card>
        </div>
      </div>
    </div>

    <!-- Winner Selection Dialog -->
    <q-dialog v-model="showWinnerDialog" persistent>
      <q-card style="min-width: 350px">
        <q-card-section class="bg-positive text-white">
          <div class="text-h6">
            <q-icon name="emoji_events" class="q-mr-sm" />
            Tender Won!
          </div>
        </q-card-section>

        <q-card-section>
          <p>Select the contractor who won this tender:</p>
          <q-select
            v-model="selectedWinnerId"
            :options="contractorOptions"
            emit-value
            map-options
            label="Winning Contractor"
            outlined
            :rules="[(val) => !!val || 'Please select a contractor']"
          />
          <p class="text-caption text-grey q-mt-md">
            All other contractors will be automatically marked as lost.
          </p>
        </q-card-section>

        <q-card-actions align="right">
          <q-btn flat label="Cancel" @click="cancelWinnerDialog" />
          <q-btn color="positive" label="Confirm Winner" @click="confirmWinner" />
        </q-card-actions>
      </q-card>
    </q-dialog>
  </div>
</template>

<script setup>
  import { ref, computed, watch, onMounted } from 'vue';
  import { useQuasar, Notify } from 'quasar';
  import { useTenderStore } from '@/stores/tenders';
  import { useSitesStore } from '@/stores/sites';

  const props = defineProps({
    tender: {
      type: Object,
      required: true,
    },
  });

  const emit = defineEmits(['edit-tender', 'add-contractor', 'add-quote', 'update-tender']);

  const $q = useQuasar();
  const tenderStore = useTenderStore();
  const sitesStore = useSitesStore();

  // Editable state
  const editableSiteId = ref(props.tender.site_id);
  const editableReference = ref(props.tender.reference_number);
  const editableDescription = ref(props.tender.description);
  const editableStatus = ref(props.tender.status);
  const editableEstimatedValue = ref(props.tender.estimated_value);
  const editableDueDate = ref(props.tender.due_date);
  const editableWinLikelihood = ref(props.tender.win_likelihood || 0);
  const editableProjectType = ref(props.tender.project_type);
  const editablePriority = ref(props.tender.priority);

  // Winner selection dialog state
  const showWinnerDialog = ref(false);
  const pendingWonStatus = ref(null);
  const selectedWinnerId = ref(null);

  // Watch for tender changes to update local state
  watch(() => props.tender, (newTender) => {
    editableSiteId.value = newTender.site_id;
    editableReference.value = newTender.reference_number;
    editableDescription.value = newTender.description;
    editableStatus.value = newTender.status;
    editableEstimatedValue.value = newTender.estimated_value;
    editableDueDate.value = newTender.due_date;
    editableWinLikelihood.value = newTender.win_likelihood || 0;
    editableProjectType.value = newTender.project_type;
    editablePriority.value = newTender.priority;
  }, { deep: true });

  // Site options
  const siteOptions = computed(() =>
    sitesStore.sites.map((s) => ({ label: s.name, value: s.id })),
  );

  // Contractor options for winner selection
  const contractorOptions = computed(() => {
    if (!props.tender.contractors) {return [];}
    return props.tender.contractors.map((c) => ({
      label: c.name || c.business_name || c.contractor_name,
      value: c.contractor_id,
    }));
  });

  // Status options
  const statusOptions = [
    { label: 'Pending', value: 'pending' },
    { label: 'Won', value: 'won' },
    { label: 'Lost', value: 'lost' },
    { label: 'DNQ', value: 'dnq' },
    { label: 'Unknown', value: 'unknown' },
    { label: 'Withdrew', value: 'withdrew' },
    { label: 'Dead', value: 'dead' },
  ];

  function formatFieldValue(value) {
    if (!value) {return 'Not set';}
    // Replace underscores with spaces and capitalize words
    return value.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  }

  // Project type options
  const projectTypeOptions = [
    { label: 'New Construction', value: 'new_construction' },
    { label: 'Renovation', value: 'renovation' },
    { label: 'Maintenance', value: 'maintenance' },
    { label: 'Upgrade', value: 'upgrade' },
    { label: 'Service', value: 'service' },
    { label: 'Emergency', value: 'emergency' },
    { label: 'Other', value: 'other' },
  ];

  // Priority options
  const priorityOptions = [
    { label: 'Low', value: 'low' },
    { label: 'Medium', value: 'medium' },
    { label: 'High', value: 'high' },
    { label: 'Critical', value: 'critical' },
  ];

  // Save functions
  const saveSite = async (value) => {
    await updateTenderField('site_id', value);
  };

  const saveReference = async (value) => {
    await updateTenderField('reference_number', value);
  };

  const saveDescription = async (value) => {
    await updateTenderField('description', value);
  };

  const saveStatus = async (value) => {
    if (value === 'won') {
      // Show dialog to select winner
      pendingWonStatus.value = value;
      selectedWinnerId.value = null;
      showWinnerDialog.value = true;
      return;
    }
    await updateTenderField('status', value);
  };

  const confirmWinner = async () => {
    if (!selectedWinnerId.value) {
      Notify.create({
        type: 'warning',
        message: 'Please select the winning contractor',
      });
      return;
    }

    try {
      await tenderStore.updateTender(props.tender.id, {
        status: 'won',
        winning_contractor_id: selectedWinnerId.value,
      });
      showWinnerDialog.value = false;
      Notify.create({
        type: 'positive',
        message: 'Tender marked as won',
      });
      emit('update-tender');
    } catch (error) {
      Notify.create({
        type: 'negative',
        message: 'Failed to update tender: ' + error.message,
      });
    }
  };

  const cancelWinnerDialog = () => {
    showWinnerDialog.value = false;
    editableStatus.value = props.tender.status;
  };

  const saveEstimatedValue = async (value) => {
    // Parse formatted numbers like "30,000" -> 30000
    let parsedValue = value;
    if (typeof value === 'string') {
      // Remove commas and spaces, then parse as float
      parsedValue = parseFloat(value.replace(/[,\s]/g, '')) || null;
    }
    await updateTenderField('estimated_value', parsedValue);
  };

  const saveDueDate = async (value) => {
    await updateTenderField('due_date', value);
  };

  const saveWinLikelihood = async (value) => {
    await updateTenderField('win_likelihood', value);
  };

  const saveProjectType = async (value) => {
    await updateTenderField('project_type', value);
  };

  const savePriority = async (value) => {
    await updateTenderField('priority', value);
  };

  // Generic update function
  const updateTenderField = async (field, value) => {
    try {
      await tenderStore.updateTender(props.tender.id, { [field]: value });
      // Note: No need to fetchTender() here - updateTender() already updates the store
      // and returns the updated tender. Calling fetchTender() causes race conditions
      // where fields can be cleared due to the watcher firing multiple times.
      // Only fetch when updating site_id to get the computed site_name.
      if (field === 'site_id') {
        await tenderStore.fetchTender(props.tender.id);
      }
      Notify.create({
        type: 'positive',
        message: 'Saved',
        timeout: 1000,
        position: 'top-right',
      });
      emit('update-tender');
    } catch (error) {
      Notify.create({
        type: 'negative',
        message: 'Failed to save: ' + error.message,
        position: 'top-right',
      });
      throw error;
    }
  };

  // Load sites if not already loaded
  onMounted(async () => {
    if (sitesStore.sites.length === 0) {
      await sitesStore.fetchSites();
    }
  });

  // Computed properties
  const contractorCount = computed(() => {
    return props.tender.contractors?.length || 0;
  });

  const quoteCount = computed(() => {
    return props.tender.latest_quotes?.length || 0;
  });

  const documentCount = computed(() => {
    return props.tender.documents?.length || 0;
  });

  const targetContractorCount = computed(() => {
    // Dynamic target based on project type
    const targets = {
      emergency: 2,
      maintenance: 3,
      upgrade: 4,
      renovation: 5,
      new_construction: 6,
    };
    return targets[props.tender.project_type] || 3;
  });

  const targetQuoteCount = computed(() => {
    return Math.max(contractorCount.value, 1);
  });

  const targetDocumentCount = computed(() => 3);

  const contractorProgress = computed(() => {
    return Math.min(contractorCount.value / targetContractorCount.value, 1);
  });

  const quoteProgress = computed(() => {
    return targetQuoteCount.value > 0 ? Math.min(quoteCount.value / targetQuoteCount.value, 1) : 0;
  });

  const documentProgress = computed(() => {
    return Math.min(documentCount.value / targetDocumentCount.value, 1);
  });

  // Methods
  function getStatusColor(status) {
    const colors = {
      open: 'grey',
      pending: 'orange',
      submitted: 'blue',
      won: 'positive',
      lost: 'negative',
    };
    return colors[status] || 'grey';
  }

  function getStatusIcon(status) {
    const icons = {
      open: 'folder_open',
      pending: 'schedule',
      submitted: 'send',
      won: 'emoji_events',
      lost: 'close',
    };
    return icons[status] || 'help';
  }

  function getStatusLabel(status) {
    const labels = {
      open: 'Open',
      pending: 'Pending',
      submitted: 'Submitted',
      won: 'Won',
      lost: 'Lost',
    };
    return labels[status] || status;
  }

  function getLikelihoodColor(likelihood) {
    if (likelihood >= 75) {return 'positive';}
    if (likelihood >= 50) {return 'warning';}
    if (likelihood >= 25) {return 'orange';}
    return 'negative';
  }

  function getLikelihoodLabel(likelihood) {
    if (likelihood >= 75) {return 'High';}
    if (likelihood >= 50) {return 'Medium';}
    if (likelihood >= 25) {return 'Low';}
    return 'Very Low';
  }

  function getDueDateColor(dueDate) {
    if (!dueDate) {return 'grey';}
  
    const due = new Date(dueDate);
    const now = new Date();
    const diffDays = Math.ceil((due - now) / (1000 * 60 * 60 * 24));
  
    if (diffDays < 0) {return 'negative';}
    if (diffDays <= 7) {return 'warning';}
    return 'positive';
  }

  function getDaysRemaining(dueDate) {
    if (!dueDate) {return 'Not set';}
  
    const due = new Date(dueDate);
    const now = new Date();
    const diffDays = Math.ceil((due - now) / (1000 * 60 * 60 * 24));
  
    if (diffDays < 0) {return `${Math.abs(diffDays)} days overdue`;}
    if (diffDays === 0) {return 'Due today';}
    if (diffDays === 1) {return 'Due tomorrow';}
    return `${diffDays} days left`;
  }

  function formatDate(dateString) {
    if (!dateString) {return '';}
    const date = new Date(dateString);
    return date.toLocaleDateString('en-AU', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric', 
    });
  }

  function getRelativeDate(dateString) {
    if (!dateString) {return '';}
  
    const date = new Date(dateString);
    const now = new Date();
    const diffDays = Math.ceil((date - now) / (1000 * 60 * 60 * 24));
  
    if (diffDays < 0) {return `${Math.abs(diffDays)} days ago`;}
    if (diffDays === 0) {return 'Today';}
    if (diffDays === 1) {return 'Tomorrow';}
    if (diffDays <= 7) {return `In ${diffDays} days`;}
  
    return '';
  }

  function formatCurrency(amount) {
    return Number(amount).toLocaleString('en-AU', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
  }

  function formatProjectType(type) {
    const types = {
      new_construction: 'New Construction',
      renovation: 'Renovation',
      maintenance: 'Maintenance',
      upgrade: 'Upgrade',
      emergency: 'Emergency',
      other: 'Other',
    };
    return types[type] || type || 'Not specified';
  }

  function formatPriority(priority) {
    const priorities = {
      low: 'Low',
      medium: 'Medium',
      high: 'High',
      critical: 'Critical',
    };
    return priorities[priority] || priority || 'Medium';
  }

  function getPriorityColor(priority) {
    const colors = {
      low: 'blue-grey',
      medium: 'blue',
      high: 'orange',
      critical: 'negative',
    };
    return colors[priority] || 'blue';
  }

  function sendEmail() {
    $q.notify({
      color: 'info',
      message: 'Email functionality will be implemented',
      timeout: 3000,
    });
  }

  function formatTenderType(type) {
    const types = {
      plan_spec: 'Plan & Spec',
      d_and_c: 'Design & Construct',
      open_tender: 'Open Tender',
      negotiated: 'Negotiated',
      budget_only: 'Budget Only',
    };
    return types[type] || type || null;
  }

  function formatBuilderStatus(status) {
    const statuses = {
      won: 'Won',
      lost: 'Lost',
      unknown: 'Unknown',
      withdrew: 'Withdrew',
    };
    return statuses[status] || status || 'Unknown';
  }

  function getBuilderStatusColor(status) {
    const colors = {
      won: 'positive',
      lost: 'negative',
      unknown: 'grey',
      withdrew: 'orange',
    };
    return colors[status] || 'grey';
  }

  function emailContact(email) {
    if (email) {
      window.open(`mailto:${email}`);
    }
  }
</script>

<style lang="scss" scoped>
.tender-overview-panel {
  .metrics-row {
    .metric-card {
      height: 140px;

      .q-card__section {
        display: flex;
        flex-direction: column;
        height: 100%;
        padding: 12px 16px;
      }

      // Row 1: Icon/Visual area - fixed height to accommodate circular progress
      .q-icon:first-child,
      .likelihood-display {
        height: 60px;
        display: flex;
        align-items: center;
        justify-content: center;
        margin-bottom: 8px;
        margin-left: auto;
        margin-right: auto;
      }

      // Row 2: Label - fixed height
      .metric-label {
        font-size: 0.9rem;
        color: #666;
        font-weight: 500;
        height: 24px;
        display: flex;
        align-items: center;
        justify-content: center;
        margin-bottom: 4px;
      }

      // Row 3: Value - fixed height and relative positioning for edit icon
      .metric-value {
        font-size: 1.2rem;
        font-weight: 700;
        height: 30px;
        display: flex;
        align-items: center;
        justify-content: center;
        position: relative;

        // When used as editable-value, position edit icon absolutely
        &.editable-value {
          padding-right: 24px; // Space for absolutely positioned icon

          .edit-icon {
            position: absolute;
            right: 0;
            top: 50%;
            transform: translateY(-50%);
            margin-left: 0 !important; // Override q-ml-xs
          }
        }
      }

      .likelihood-display {
        position: relative;
        display: inline-flex;

        .likelihood-text {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          font-size: 0.9rem;
          font-weight: 700;
          color: #333;
        }
      }
    }
  }
  
  .overview-section {
    .section-header {
      display: flex;
      align-items: center;
      margin-bottom: 16px;
      padding-bottom: 8px;
      border-bottom: 1px solid #f0f0f0;
      
      .section-title {
        font-size: 1.1rem;
        font-weight: 700;
        color: #333;
      }
    }
    
    .summary-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
      gap: 16px;
      margin-bottom: 16px;
      
      .summary-item {
        .summary-label {
          font-size: 0.85rem;
          color: #666;
          font-weight: 500;
          margin-bottom: 4px;
        }
        
        .summary-value {
          font-weight: 600;
          color: #333;
        }
      }
    }
    
    .description-section {
      .description-label {
        font-size: 0.85rem;
        color: #666;
        font-weight: 500;
        margin-bottom: 8px;
        margin-top: 16px;
        padding-top: 16px;
        border-top: 1px solid #f0f0f0;
      }
      
      .description-text {
        color: #333;
        line-height: 1.5;
      }
    }
    
    .actions-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
      gap: 12px;

      .action-btn {
        height: 48px;
        font-weight: 600;
        border-radius: 8px;
      }
    }

    .tech-flags-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
      gap: 12px;

      .tech-flag-item {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 8px 12px;
        background: #f8f9fa;
        border-radius: 8px;
        font-weight: 500;

        &:hover {
          background: #eee;
        }
      }
    }

    .last-followup-content {
      background: #fff8e1;
      padding: 12px;
      border-radius: 8px;
      border-left: 4px solid #ff9800;

      .followup-date,
      .followup-user {
        display: flex;
        align-items: center;
      }
    }

    .timeline {
      .q-timeline__entry {
        .q-timeline__subtitle {
          font-size: 0.8rem;
        }
      }
    }
    
    .progress-items {
      .progress-item {
        margin-bottom: 16px;
        
        &:last-child {
          margin-bottom: 0;
        }
        
        .progress-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 8px;
          font-size: 0.9rem;
          
          .progress-count {
            font-weight: 600;
            color: #666;
          }
        }
      }
    }
  }

  // Editable field styles
  .editable-value {
    cursor: pointer;
    position: relative;
    display: inline-flex;
    align-items: center;
    padding: 2px 4px;
    border-radius: 4px;
    transition: background-color 0.2s;

    &:hover {
      background-color: rgba(0, 0, 0, 0.05);

      .edit-icon {
        opacity: 1;
      }
    }
  }

  .edit-icon {
    opacity: 0.3;
    transition: opacity 0.2s;
    color: #1976d2;
  }

  .metric-card.clickable {
    cursor: pointer;
    transition: box-shadow 0.2s;

    &:hover {
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    }
  }
}
</style>